import OtaEmailNotice, { useEmailLinkAcknowledgement } from "./OtaEmailNotice";
import React from "react";
import {
  Box,
  Button,
  CheckBox,
  Heading,
  RadioButtonGroup,
  Text,
} from "grommet";
import LocalizedEmailEditor from "./LocalizedEmailEditor";
import LocalizedTextEditor, { SMS_MAX_LENGTH } from "./LocalizedTextEditor";
import { useTranslation } from "react-i18next";

// Returns true if `text` contains the given tag, either as a {tagName} literal
// or as a tag-pill span (data-tag="tagName") emitted by the rich-text editor.
const containsTag = (text, tag) => {
  if (!text) return false;
  return text.includes(`{${tag}}`) || text.includes(`data-tag="${tag}"`);
};

export default function CheckinFlowReminder({
  communications,
  onCommunicationsChange,
  onSaveCommunications,
  savedCommunications,
  hotel,
  defaults,
  onHotelChange,
  automationManaged = false,
  channels,
}) {
  const { t } = useTranslation();
  const linkAcknowledgement = useEmailLinkAcknowledgement(communications?.reminderEmail?.body);

  const commsDisabled = !!(
    hotel?.checkinInvitationDisabled || hotel?.disableAllCommunications
  );
  const reminderEnabled = !!hotel?.reminderEnabled;
  const useCheckin = communications?.reminderUseCheckinEmail !== false;

  const handleReminderToggle = (e) => {
    onHotelChange({ reminderEnabled: e.target.checked });
  };

  const handleModeChange = (value) => {
    const updated = {
      ...communications,
      reminderUseCheckinEmail: value === "same",
    };
    onCommunicationsChange(updated);
    onSaveCommunications(updated);
  };

  return (
    <Box id="section-reminder" gap={automationManaged ? "small" : "medium"}>
      <Heading level={3} margin="none">
        {t(automationManaged ? "automation.emailAndText" : "checkinFlow.reminder.title")}
      </Heading>
      <Text size="small" color={automationManaged ? "dark-2" : "dark-4"} margin={automationManaged ? "none" : { bottom: "small" }}>
        {t(automationManaged ? "automation.descriptions.reminder" : "checkinFlow.reminder.description")}
      </Text>

      {commsDisabled ? (
        <Text size="small" color="status-critical">
          {t("checkinFlow.common.commsDisabled")}
        </Text>
      ) : (
        <>
          {!automationManaged && <CheckBox
            checked={reminderEnabled}
            onChange={handleReminderToggle}
            label={t("checkinFlow.reminder.toggle")}
            toggle
          />}

          {(automationManaged || reminderEnabled) && (
            <Box
              gap="medium"
              pad={automationManaged ? undefined : { left: "medium" }}
              margin={{ top: "small" }}
              border={automationManaged ? undefined : { side: "left", color: "light-4", size: "2px" }}
            >
              <RadioButtonGroup
                className={automationManaged ? "automation-reminder-mode" : undefined}
                gap={automationManaged ? "none" : undefined}
                name="reminderMode"
                options={[
                  {
                    label: t("checkinFlow.reminder.sameAsOnline"),
                    value: "same",
                  },
                  { label: t("checkinFlow.reminder.custom"), value: "custom" },
                ]}
                value={useCheckin ? "same" : "custom"}
                onChange={(e) => handleModeChange(e.target.value)}
              />

              {!useCheckin && (
                <>
                  <Box className={automationManaged ? "automation-editor-section automation-special-panel" : undefined} style={automationManaged && !channels?.includes("email") ? { display: "none" } : undefined} margin={automationManaged ? "none" : { top: "small" }} gap="small">
                    <Heading level={4} margin="none">
                      {t("checkinFlow.common.email")}
                    </Heading>
                    <LocalizedEmailEditor
                      value={
                        communications?.reminderEmail || defaults?.reminderEmail
                      }
                      onChange={(val) =>
                        onCommunicationsChange({
                          ...communications,
                          reminderEmail: val,
                        })
                      }
                      defaultValue={defaults?.reminderEmail}
                      useCustom={true}
                      onUseCustomChange={() => {}}
                      hideUseCustomToggle
                      template="pre-checkin"
                      upsells={communications?.reminderEmailUpsells || []}
                      currency={hotel?.currency}
                      onUpsellsChange={(upsells, email) => {
                        const updated = {
                          ...communications,
                          reminderEmail: email,
                          reminderEmailUpsells: upsells,
                        };
                        onSaveCommunications(updated);
                      }}
                      tagHint={t("checkinFlow.common.hintTags")}
                      tags={[
                        "firstName",
                        "lastName",
                        "hotel",
                        "hotelPhone",
                        "checkinDate",
                        "checkinTime",
                        "checkoutDate",
                        "checkoutTime",
                        "precheckinButton",
                        "stayDatesBox",
                        "mobileAppGuide",
                        "upsells",
                      ]}
                    />
                    <Box gap="xsmall">
                      {(() => {
                        const emailEn =
                          communications?.reminderEmail?.body?.en ??
                          defaults?.reminderEmail?.body?.en ??
                          "";
                        const emailFr =
                          communications?.reminderEmail?.body?.fr ??
                          defaults?.reminderEmail?.body?.fr ??
                          "";
                        const enEmpty = !emailEn
                          ?.replace(/<[^>]*>/g, "")
                          ?.trim();
                        const frEmpty = !emailFr
                          ?.replace(/<[^>]*>/g, "")
                          ?.trim();
                        const enMissingButton =
                          !enEmpty && !containsTag(emailEn, "precheckinButton");
                        const frMissingButton =
                          hotel?.language === "fr" &&
                          !frEmpty &&
                          !containsTag(emailFr, "precheckinButton");
                        return (
                          <>
                            {enEmpty && (
                              <Text size="small" color="status-critical">
                                {t("checkinFlow.common.emailMissing")}
                              </Text>
                            )}
                            {enMissingButton && (
                              <Text size="small" color="status-critical">
                                {t(
                                  "checkinFlow.common.precheckinButtonRequired",
                                )}
                              </Text>
                            )}
                            {hotel?.language === "fr" && frEmpty && (
                              <Text size="small" color="status-warning">
                                {t("checkinFlow.common.frEmailMissing")}
                              </Text>
                            )}
                            {hotel?.language === "fr" && frMissingButton && (
                              <Text size="small" color="status-critical">
                                {t(
                                  "checkinFlow.common.frPrecheckinButtonRequired",
                                )}
                              </Text>
                            )}
                          </>
                        );
                      })()}
                    </Box>
                    {!automationManaged && <Box align="end" gap="xsmall">
                          <OtaEmailNotice hotel={hotel} acknowledgement={linkAcknowledgement} />
                      <Text size="xsmall" color={automationManaged ? "dark-2" : "dark-4"}>
                        {t("checkinFlow.common.pressEmailSave")}
                      </Text>
                      <Button
                        label={t("checkinFlow.common.save")}
                        primary
                        size="small"
                        disabled={linkAcknowledgement.blocked || (() => {
                          const emailEn =
                            communications?.reminderEmail?.body?.en ??
                            defaults?.reminderEmail?.body?.en ??
                            "";
                          const emailFr =
                            communications?.reminderEmail?.body?.fr ??
                            defaults?.reminderEmail?.body?.fr ??
                            "";
                          const enEmpty = !emailEn
                            ?.replace(/<[^>]*>/g, "")
                            ?.trim();
                          const frEmpty = !emailFr
                            ?.replace(/<[^>]*>/g, "")
                            ?.trim();
                          const enMissingButton =
                            !enEmpty &&
                            !containsTag(emailEn, "precheckinButton");
                          const frMissingButton =
                            hotel?.language === "fr" &&
                            !frEmpty &&
                            !containsTag(emailFr, "precheckinButton");
                          const hasError =
                            enEmpty ||
                            enMissingButton ||
                            (hotel?.language === "fr" &&
                              (frEmpty || frMissingButton));
                          return (
                            hasError ||
                            JSON.stringify(communications?.reminderEmail) ===
                              JSON.stringify(savedCommunications?.reminderEmail)
                          );
                        })()}
                        onClick={() => onSaveCommunications()}
                      />
                    </Box>}
                  </Box>

                  <Box className={automationManaged ? "automation-editor-section automation-special-panel" : undefined} style={automationManaged && !channels?.includes("sms") ? { display: "none" } : undefined} margin={automationManaged ? "none" : { top: "medium" }} gap="small">
                    <Heading level={4} margin="none">
                      {t("checkinFlow.common.textMessage")}
                    </Heading>
                    <LocalizedTextEditor
                      value={
                        communications?.reminderText || defaults?.reminderText
                      }
                      onChange={(val) =>
                        onCommunicationsChange({
                          ...communications,
                          reminderText: val,
                        })
                      }
                      defaultValue={defaults?.reminderText}
                      useCustom={true}
                      onUseCustomChange={() => {}}
                      hideUseCustomToggle
                      hint={t("checkinFlow.common.hintTags")}
                      maxLength={SMS_MAX_LENGTH}
                      tags={[
                        "firstName",
                        "lastName",
                        "hotel",
                        "checkinDate",
                        "checkinTime",
                        "checkoutDate",
                        "checkoutTime",
                        "magicLink",
                      ]}
                    />
                    <Box gap="xsmall">
                      {(() => {
                        const textEn =
                          communications?.reminderText?.en ??
                          defaults?.reminderText?.en ??
                          "";
                        const textFr =
                          communications?.reminderText?.fr ??
                          defaults?.reminderText?.fr ??
                          "";
                        const enEmpty = !textEn?.trim();
                        const frEmpty = !textFr?.trim();
                        const enMissingLink =
                          !enEmpty && !containsTag(textEn, "magicLink");
                        const frMissingLink =
                          hotel?.language === "fr" &&
                          !frEmpty &&
                          !containsTag(textFr, "magicLink");
                        return (
                          <>
                            {enEmpty && (
                              <Text size="small" color="status-critical">
                                {t("checkinFlow.common.textMissing")}
                              </Text>
                            )}
                            {enMissingLink && (
                              <Text size="small" color="status-critical">
                                {t("checkinFlow.common.magicLinkRequired")}
                              </Text>
                            )}
                            {hotel?.language === "fr" && frEmpty && (
                              <Text size="small" color="status-warning">
                                {t("checkinFlow.common.frTextMissing")}
                              </Text>
                            )}
                            {hotel?.language === "fr" && frMissingLink && (
                              <Text size="small" color="status-critical">
                                {t("checkinFlow.common.frMagicLinkRequired")}
                              </Text>
                            )}
                          </>
                        );
                      })()}
                    </Box>
                    {!automationManaged && <Box align="end" gap="xsmall">
                      <Text size="xsmall" color={automationManaged ? "dark-2" : "dark-4"}>
                        {t("checkinFlow.common.pressTextSave")}
                      </Text>
                      <Button
                        label={t("checkinFlow.common.save")}
                        primary
                        size="small"
                        disabled={(() => {
                          const textEn =
                            communications?.reminderText?.en ??
                            defaults?.reminderText?.en ??
                            "";
                          const textFr =
                            communications?.reminderText?.fr ??
                            defaults?.reminderText?.fr ??
                            "";
                          const enEmpty = !textEn?.trim();
                          const frEmpty = !textFr?.trim();
                          const enMissingLink =
                            !enEmpty && !containsTag(textEn, "magicLink");
                          const frMissingLink =
                            hotel?.language === "fr" &&
                            !frEmpty &&
                            !containsTag(textFr, "magicLink");
                          const tooLongEn =
                            (textEn || "").length > SMS_MAX_LENGTH;
                          const tooLongFr =
                            (textFr || "").length > SMS_MAX_LENGTH;
                          const hasError =
                            enEmpty ||
                            enMissingLink ||
                            tooLongEn ||
                            (hotel?.language === "fr" &&
                              (frEmpty || frMissingLink || tooLongFr));
                          return (
                            hasError ||
                            JSON.stringify(communications?.reminderText) ===
                              JSON.stringify(savedCommunications?.reminderText)
                          );
                        })()}
                        onClick={() => onSaveCommunications()}
                      />
                    </Box>}
                  </Box>
                </>
              )}
            </Box>
          )}
        </>
      )}
    </Box>
  );
}
